import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";

export const OrderTypes = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Order Types</h1>

        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn about the different order types available on Nordex</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-6">
            Nordex supports market orders and limit orders. You can also attach Take Profit and Stop
            Loss to any position.
          </p>
        </section>

        {/* Market Orders */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Market Orders</h2>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold text-lg mb-3">What Is a Market Order?</h3>
            <p className="text-sm text-muted-foreground">
              A market order executes immediately at the best available current market price. When
              you click "Long" or "Short" in the Order Form, you're placing a market order.
            </p>
          </div>

          <h3 className="text-lg font-semibold mb-4">How Market Orders Work</h3>
          <div className="space-y-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h4 className="font-semibold mb-1">You Submit the Order</h4>
                <p className="text-sm text-muted-foreground">
                  Click "Long" or "Short" with your desired collateral and leverage settings.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h4 className="font-semibold mb-1">Immediate Execution</h4>
                <p className="text-sm text-muted-foreground">
                  The order executes instantly at the current market price shown in the order form.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h4 className="font-semibold mb-1">Position Opened</h4>
                <p className="text-sm text-muted-foreground">
                  Your position appears in the Positions panel with the executed entry price.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-4">Advantages</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">Speed:</strong> Execute instantly without waiting
            </li>
            <li>
              <strong className="text-foreground">Guaranteed Execution:</strong> Your order will
              fill (assuming sufficient liquidity)
            </li>
            <li>
              <strong className="text-foreground">Simplicity:</strong> No need to set specific price
              levels
            </li>
            <li>
              <strong className="text-foreground">Best for Volatile Markets:</strong> Ensures you get
              into positions quickly when prices are moving fast
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Disadvantages</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">No Price Control:</strong> You get whatever the
              current market price is
            </li>
            <li>
              <strong className="text-foreground">Slippage Risk:</strong> In volatile or
              low-liquidity conditions, execution price may differ from displayed price
            </li>
            <li>
              <strong className="text-foreground">Higher Fees:</strong> Market orders incur taker
              fees instead of the lower maker fees (see{" "}
              <a href="/fees" className="text-primary hover:underline">
                Fee Structure
              </a>
              )
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">When to Use Market Orders</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="p-4 border border-border rounded-lg bg-card">
              <h4 className="font-semibold mb-2 text-emerald-500">✓ Good For</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Quick entries during breakouts</li>
                <li>• Closing positions fast</li>
                <li>• Highly liquid markets</li>
                <li>• When speed matters more than price</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg bg-card">
              <h4 className="font-semibold mb-2 text-red-500">✗ Avoid When</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Trading large positions</li>
                <li>• During flash crashes or spikes</li>
                <li>• When you have a specific target price</li>
                <li>• In illiquid markets</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Limit Orders */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Limit Orders</h2>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold text-lg mb-3">What Is a Limit Order?</h3>
            <p className="text-sm text-muted-foreground">
              A limit order lets you set a specific price at which you want your order to execute.
              The order fills when the market reaches your price, giving you precise control over
              entry and exit levels.
            </p>
          </div>

          <h3 className="text-lg font-semibold mb-4">Fill Behavior</h3>
          <p className="text-muted-foreground mb-6">
            Limit orders fill at your specified limit price, not the current market price. For a buy
            limit, the order executes when the market price falls to or below your limit. For a sell
            limit, it executes when the market price rises to or above your limit.
          </p>

          <h3 className="text-lg font-semibold mb-4">Time-in-Force Options</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">GTC (Good Till Canceled):</strong> The order stays
              open until it is filled or you manually cancel it.
            </li>
            <li>
              <strong className="text-foreground">ALO (Add Liquidity Only / Post-Only):</strong> The
              order only adds liquidity to the book and never crosses the spread. If it would
              execute immediately, it is rejected. This ensures you pay maker fees instead of taker
              fees.
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Advantages</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">Price Control:</strong> You know exactly what
              price you will get
            </li>
            <li>
              <strong className="text-foreground">Lower Fees:</strong> Limit orders typically pay
              maker fees, which are lower than taker fees
            </li>
            <li>
              <strong className="text-foreground">Set-and-Forget:</strong> Place your order and wait
              for the market to come to you
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Disadvantages</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">No Guaranteed Fill:</strong> The market may never
              reach your price
            </li>
            <li>
              <strong className="text-foreground">May Miss Fast Moves:</strong> In rapidly moving
              markets, your limit may be bypassed
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">When to Use Limit Orders</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="p-4 border border-border rounded-lg bg-card">
              <h4 className="font-semibold mb-2 text-emerald-500">✓ Good For</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Specific entry or exit prices</li>
                <li>• Range trading</li>
                <li>• Fee optimization (maker fees)</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg bg-card">
              <h4 className="font-semibold mb-2 text-red-500">✗ Avoid When</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• When you need immediate execution</li>
                <li>• During strong directional moves</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Slippage */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Slippage Tolerance</h2>

          <p className="text-muted-foreground mb-6">
            Slippage occurs when the execution price differs from the expected price shown when you
            submit the order. This happens because market prices can move between when you click the
            button and when the order executes.
          </p>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold mb-4">Example of Slippage</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Expected Price (when clicked):</span>
                <span className="font-semibold">$50,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Actual Execution Price:</span>
                <span className="font-semibold">$50,025</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <span className="font-semibold">Slippage:</span>
                <span className="font-bold text-amber-500">$25 (0.05%)</span>
              </div>
            </div>
          </div>

          <p className="text-muted-foreground mb-4">
            Configure your slippage tolerance in the settings (gear icon) with presets: Auto, 0.1%,
            0.25%, 0.5%, and 1%. Slippage tolerance applies to market orders only.
          </p>

          <h3 className="text-lg font-semibold mb-4">Minimizing Slippage</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Trade during high-liquidity periods (when volume is high)</li>
            <li>Avoid placing large orders relative to available liquidity</li>
            <li>Check the order book depth before placing large orders</li>
            <li>Consider using limit orders for more price control</li>
          </ul>
        </section>

        {/* Take Profit / Stop Loss */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Take Profit / Stop Loss</h2>

          <p className="text-muted-foreground mb-6">
            TP/SL can be set when placing an order (checkbox in the order form) or edited on open
            positions (pencil icon in the TP/SL column of the Positions panel, or by dragging lines
            on the chart).
          </p>

          <h3 className="text-lg font-semibold mb-4">Standard TP/SL</h3>
          <p className="text-muted-foreground mb-4">
            Set a single take-profit price and/or stop-loss price. Use ROI-based inputs with a
            slider, or enter the price directly. Expected P&L is shown before confirming.
          </p>

          <h3 className="text-lg font-semibold mb-4">Partial TP/SL</h3>
          <p className="text-muted-foreground mb-4">
            Set multiple take-profit and stop-loss levels at different prices. Each target specifies
            a trigger price and close amount (by percentage of position or absolute USD). For
            example: TP1 closes 50% at +10% ROI, TP2 closes remaining at +20% ROI. Targets are shown
            as labeled lines on the chart (TP1, TP2, SL1, etc.).
          </p>

          <div className="p-4 border border-border rounded-lg bg-card">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Note:</strong> TP/SL and Reduce-Only are mutually
              exclusive — enabling one disables the other.
            </p>
          </div>
        </section>

        {/* Reduce-Only Orders */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Reduce-Only Orders</h2>

          <p className="text-muted-foreground">
            A toggle in the order form. When enabled, the order can only reduce an existing
            position, not increase it or open a new one. Useful for closing or partially closing
            positions.
          </p>
        </section>

        {/* Fees */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Trading Fees</h2>

          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Taker Fee (Market Orders)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Market orders are "taker" orders because they take liquidity from the order book.
              </p>
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
                <strong>Fee:</strong> Taker fee applied to position size (see{" "}
                <a href="/fees" className="text-primary hover:underline">
                  Fee Structure
                </a>
                )
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Maker Fee (Limit Orders)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Limit orders add liquidity to the book and pay the lower maker fee.
              </p>
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm">
                <strong>Fee:</strong> Maker fee applied to position size (lower than taker; see{" "}
                <a href="/fees" className="text-primary hover:underline">
                  Fee Structure
                </a>
                )
              </div>
            </div>
          </div>

          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Fee Calculation Example</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Position Size:</span>
                <span className="font-semibold">$10,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Market Order (Taker) Fee:</span>
                <span className="font-semibold">Taker fee % of position size</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold">Details:</span>
                <a href="/fees" className="font-bold text-primary hover:underline">
                  See Fee Structure
                </a>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              The fee is deducted from your collateral when you open the position.
            </p>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Best Practices</h2>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Double-check before submitting</strong> — always
              review your collateral amount, leverage, position size, and estimated fees before
              clicking Long or Short.
            </li>
            <li>
              <strong className="text-foreground">Be aware of market conditions</strong> — during
              high volatility or low liquidity, market orders may experience more slippage than
              usual.
            </li>
            <li>
              <strong className="text-foreground">Monitor liquidation price</strong> — before
              placing an order, check the calculated liquidation price to ensure you're comfortable
              with the risk level.
            </li>
            <li>
              <strong className="text-foreground">Factor in fees</strong> — trading fees are
              deducted from your collateral, so make sure you have sufficient balance including
              fees.
            </li>
          </ul>
        </section>

        <DocsNavigation
          previous={{ title: "Funding Rates", href: "/funding-rates" }}
          next={{ title: "Position Management", href: "/position-management" }}
        />
      </div>
    </DocsLayout>
  );
};
